
import { useState, useRef, useEffect } from "react";
import ArmDiagram, { AXES } from "./ArmDiagram";
import StepCard from "./StepCard";

export const SequencePanel = ({ steps, onDeleteStep, onMoveStep }) => (
  <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>Sequence</div>
      <span style={{ fontSize: 11, color: "#94a3b8" }}>{steps.length} step{steps.length !== 1 ? "s" : ""}</span>
    </div>
    {steps.length === 0 ? (
      <div style={{ border: "1.5px dashed #e2e8f0", borderRadius: 10, padding: "20px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
        No steps yet — add arm or service steps above.
      </div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {steps.map((step, i) => (
          <StepCard
            key={step.id} step={step} index={i} total={steps.length}
            onDelete={() => onDeleteStep(step.id)}
            onMoveUp={() => onMoveStep(i, -1)}
            onMoveDown={() => onMoveStep(i, 1)}
          />
        ))}
      </div>
    )}
  </div>
);

const SliderScreen = ({
  values, setValues, interval, setInterval, steps,
  onMarkArmStep, onReset, apiStatus, activeAxis, setActiveAxis,
  onDeleteStep, onMoveStep, onOpenServiceModal, onJointMovement, moveStatus,
}) => (
  <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 16, alignItems: "start", padding: "18px 20px", flex: 1, overflowY: "auto" }}>
    {/* Left: diagram sticky */}
    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 8px", position: "sticky", top: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", textAlign: "center", marginBottom: 2 }}>Joint Diagram</div>
      <div style={{ fontSize: 10, color: "#94a3b8", textAlign: "center", marginBottom: 8, minHeight: 13 }}>
        {activeAxis ? `→ ${AXES.find(a => a.id === activeAxis)?.label}` : "Hover a slider"}
      </div>
      <div style={{ height: 260 }}><ArmDiagram activeAxis={activeAxis} values={values} /></div>
      {moveStatus && (
        <div style={{ marginTop: 10, padding: 8, borderRadius: 6, background: moveStatus === 'sending' ? '#fffbeb' : moveStatus === 'success' ? '#dcfce7' : '#fee2e2', border: '1px solid ' + (moveStatus === 'sending' ? '#fde68a' : moveStatus === 'success' ? '#bbf7d0' : '#fecaca'), fontSize: 10, color: moveStatus === 'sending' ? '#92400e' : moveStatus === 'success' ? '#166534' : '#991b1b', textAlign: 'center' }}>
          {moveStatus === 'sending' ? 'Sending...' : moveStatus === 'success' ? '✓ Moved!' : '✗ Failed'}
        </div>
      )}
    </div>

    {/* Right: controls + sequence */}
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Joint sliders */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Joint Positions</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9}}>
          {AXES.map((axis) => (
            <AxisSlider key={axis.id} axis={axis} value={values[axis.id]} isActive={activeAxis === axis.id}
              onChange={(v) => setValues(p => ({ ...p, [axis.id]: v }))}
              onFocus={() => setActiveAxis(axis.id)} 
              onBlur={() => setActiveAxis(null)} 
              onMove={(updateValue) => {  onJointMovement({ ...values, [axis.id]: updateValue });  }}
              // disabled={moveStatus === 'sending'}
            />
          ))}
        </div>
        {/* Interval */}
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ fontSize: 12, color: "#64748b", fontWeight: 500, whiteSpace: "nowrap" }}>Interval (ms)</label>
          <input
            type="number" min={0} max={10000} step={50} value={interval}
            onChange={e => setInterval(Number(e.target.value))}
            style={{ width: 90, padding: "6px 10px", borderRadius: 7, border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a", outline: "none", background: "white" }}
          />
        </div>
      </div>

      {/* Tip */}
      <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 9, padding: "9px 13px", display: "flex", gap: 7 }}>
        <span style={{ fontSize: 13 }}>💡</span>
        <span style={{ fontSize: 11, color: "#92400e" }}>
          <strong>Tip:</strong> Set joint positions, then click <strong>+ Add arm step</strong> to record. Use <strong>+ Add service step</strong> to interleave BLE service characteristics anywhere in the sequence.
        </span>
      </div>

      {/* Action buttons */}
      <ActionButtons
        onMarkArmStep={onMarkArmStep}
        onOpenServiceModal={onOpenServiceModal}
        onReset={onReset}
        apiStatus={apiStatus}
      />

      {/* Unified sequence */}
      <SequencePanel steps={steps} onDeleteStep={onDeleteStep} onMoveStep={onMoveStep} />
    </div>
  </div>
);


export default SliderScreen;




// ── Action Buttons Row (Reset | + Add arm step | + Add service step) ──────────
export const ActionButtons = ({ onMarkArmStep, onOpenServiceModal, onReset, apiStatus }) => (
  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
    <button
      onClick={onReset}
      style={{ padding: "9px 16px", background: "white", border: "1px solid #e2e8f0", color: "#64748b", borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 500, flexShrink: 0 }}
      onMouseEnter={e => { e.currentTarget.style.borderColor="#fca5a5"; e.currentTarget.style.color="#EF4444"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor="#e2e8f0"; e.currentTarget.style.color="#64748b"; }}>
      Reset
    </button>
    <button
      onClick={onMarkArmStep}
      style={{
        flex: 1, padding: "9px 14px",
        background: apiStatus === "ok" ? "#10B981" : "#3B82F6",
        border: "none", color: "white", borderRadius: 9, cursor: "pointer",
        fontSize: 12, fontWeight: 600, transition: "background 0.3s",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
      }}>
      {apiStatus === "sending" ? "Saving…" : apiStatus === "ok" ? "✓ Saved!" : apiStatus === "error" ? "✗ Error" : <><span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add arm step</>}
    </button>
    <button
      onClick={onOpenServiceModal}
      style={{
        flex: 1, padding: "9px 14px",
        background: "#10B981",
        border: "none", color: "white", borderRadius: 9, cursor: "pointer",
        fontSize: 12, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
      }}
      onMouseEnter={e => e.currentTarget.style.background="#059669"}
      onMouseLeave={e => e.currentTarget.style.background="#10B981"}>
      <span style={{ fontSize: 14, lineHeight: 1 }}>+</span> Add service step
    </button>
  </div>
);



// ── Axis Slider ───────────────────────────────────────────────────────────────
const AxisSlider = ({ axis, value, onChange, isActive, onFocus, onBlur, disabled,onMove }) => {
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);
  const pct = (value / 180) * 100;

  const handleChange = (newValue) => {
    onChange(newValue);
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
    onMove(newValue);
    }, 200);
  };

  useEffect(() => {
    return () => clearTimeout(debounceTimerRef.current);
  }, []);

    useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      if (disabled) return;
      e.preventDefault(); // stops page from scrolling
      const delta = e.deltaY > 0 ? -1 : 1;
      handleChange(Math.min(180, Math.max(0, value + delta)));
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [value, disabled]);

  const handleKeyDown = (e) => {
    if (disabled) return;


    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      handleChange(Math.max(0, value - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      handleChange(Math.min(180, value + 1));
    } else {
      return;
    }

  };


  return (
    <div style={{ padding: "11px 13px", borderRadius: 10, border: `1.5px solid ${disabled ? "#d1d5db" : isActive ? axis.color : "#e2e8f0"}`, background: disabled ? "#f3f4f6" : isActive ? `${axis.color}09` : "white", transition: "border-color 0.2s, background 0.2s", opacity: disabled ? 0.6 : 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 7 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: disabled ? "#9ca3af" : isActive ? axis.color : "#374151" }}>{axis.label}</div>
          <div style={{ fontSize: 10, color: disabled ? "#d1d5db" : "#94a3b8", marginTop: 1 }}>{axis.description}</div>
        </div>
        <span style={{ fontSize: 15, fontWeight: 700, color: disabled ? "#9ca3af" : isActive ? axis.color : "#1e293b", minWidth: 40, textAlign: "right" }}>{value}°</span>
      </div>
      <div style={{ position: "relative", height: 18, display: "flex", alignItems: "center", opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
        <div style={{ position: "absolute", left: 0, right: 0, height: 4, background: "#f1f5f9", borderRadius: 2 }} />
        <div style={{ position: "absolute", left: 0, width: `${pct}%`, height: 4, background: isActive ? `linear-gradient(90deg,${axis.color}55,${axis.color})` : "linear-gradient(90deg,#93c5fd,#3B82F6)", borderRadius: 2 }} />
        <input 
          ref={inputRef}
          type="range" 
          min={0} 
          max={180} 
          value={value}
          onChange={(e) => !disabled && handleChange(Number(e.target.value))}
          // onMouseEnter={handleMouseEnter}
          // onMouseLeave={handleMouseLeave}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{ width: "100%", appearance: "none", background: "transparent", height: 18, cursor: disabled ? "not-allowed" : "pointer", position: "relative", zIndex: 1, "--c": disabled ? "#d1d5db" : isActive ? axis.color : "#3B82F6", opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto" }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        {["0°","45°","90°","135°","180°"].map(l => <span key={l} style={{ fontSize: 9, color: disabled ? "#e5e7eb" : "#d1d5db" }}>{l}</span>)}
      </div>
    </div>
  );
};
